import { API } from '../api/API';
import useAuthStore from '../store/authStore';

/**
 * @typedef {Object} ActeMedical
 * @property {number} id - ID auto-généré de l'acte médical
 * @property {string} code - Code de l'acte médical
 * @property {number} prix - Prix de l'acte médical
 * @property {string} label - Libellé de l'acte médical
 * @property {string} description - Description de l'acte médical
 * @property {string} categorie - Catégorie de l'acte médical
 */

const acteMedicalService = {
  /**
   * Récupère tous les actes médicaux avec pagination
   * @param {number} page - Numéro de la page (défaut: 1)
   * @param {number} limit - Nombre d'éléments par page (défaut: 10)
   * @param {Object} filters - Filtres optionnels
   * @returns {Promise<{success: boolean, data: ActeMedical[], pagination: Object}>}
   */
  getAllActesMedicaux: async (page = 1, limit = 10, filters = {}) => {
    try {
      const params = {
        page,
        limit,
        ...filters
      };
      
      const response = await API.get('/actes-medicaux', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des actes médicaux:', error);
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des actes médicaux',
        details: error.response?.data?.details
      };
    }
  },

  /**
   * Crée un nouvel acte médical
   * @param {Object} acteData - Données de l'acte médical
   * @param {string} acteData.code - Code de l'acte
   * @param {number} acteData.prix - Prix de l'acte
   * @param {string} acteData.label - Libellé de l'acte (requis)
   * @param {string} acteData.description - Description de l'acte
   * @param {string} acteData.categorie - Catégorie de l'acte (requis)
   * @returns {Promise<{success: boolean, data: ActeMedical, message: string}>}
   */
  createActeMedical: async (acteData) => {
    try {
      const response = await API.post('/actes-medicaux', acteData);
      return {
        success: true,
        data: response.data,
        message: 'Acte médical créé avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la création de l\'acte médical',
        details: error.response?.data?.details
      };
    }
  },

  /**
   * Récupère un acte médical par son ID
   * @param {number} id - ID de l'acte médical
   * @returns {Promise<{success: boolean, data: ActeMedical}>}
   */
  getActeMedicalById: async (id) => {
    try {
      const response = await API.get(`/actes-medicaux/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération de l\'acte médical',
        details: error.response?.data?.details
      };
    }
  },

  /**
   * Met à jour un acte médical
   * @param {number} id - ID de l'acte médical à mettre à jour
   * @param {Object} updateData - Données à mettre à jour
   * @returns {Promise<{success: boolean, data: ActeMedical, message: string}>}
   */
  updateActeMedical: async (id, updateData) => {
    try {
      const response = await API.put(`/actes-medicaux/${id}`, updateData);
      return {
        success: true,
        data: response.data,
        message: 'Acte médical mis à jour avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la mise à jour de l\'acte médical',
        details: error.response?.data?.details
      };
    }
  },

  /**
   * Supprime un acte médical
   * @param {number} id - ID de l'acte médical à supprimer
   * @returns {Promise<{success: boolean, message: string}>}
   */
  deleteActeMedical: async (id) => {
    try {
      await API.delete(`/actes-medicaux/${id}`);
      return {
        success: true,
        message: 'Acte médical supprimé avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la suppression de l\'acte médical',
        details: error.response?.data?.details
      };
    }
  },

  /**
   * Récupère les actes médicaux par catégorie
   * @param {string} categorie - Catégorie des actes médicaux
   * @param {number} page - Numéro de la page (défaut: 1)
   * @param {number} limit - Nombre d'éléments par page (défaut: 10)
   * @returns {Promise<{success: boolean, data: ActeMedical[], pagination: Object}>}
   */
  getActesMedicauxByCategorie: async (categorie, page = 1, limit = 10) => {
    try {
      const params = {
        page,
        limit
      };
      
      const response = await API.get(`/actes-medicaux/categorie/${categorie}`, { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des actes médicaux par catégorie',
        details: error.response?.data?.details
      };
    }
  }
};

export default acteMedicalService;